import { useEffect, useState } from "react";
import type { AttendanceRecord, ManagedUser } from "@platform/contracts";
import { api } from "../api";
import type { NotificationKind } from "./Portal";
import { useLiveData } from "../useLiveData";
import { formatDate, formatTime } from "../dates";
import { documentShell, escapeHtml, openPrintWindow } from "../printing";
import { DateField } from "./DateField";

function isoDate(date: Date): string { return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`; }

export function PunchesPanel({ canManageUsers, onMessage }: { canManageUsers: boolean; onMessage: (message: string, kind?: NotificationKind) => void }) {
  const [records, setRecords] = useState<AttendanceRecord[]>([]);
  const [users, setUsers] = useState<ManagedUser[]>([]);
  const [userId, setUserId] = useState("");
  const [from, setFrom] = useState(() => isoDate(new Date(Date.now() - 6 * 86400000)));
  const [to, setTo] = useState(() => isoDate(new Date()));
  const [loading, setLoading] = useState(false);
  const report = (error: Error) => onMessage(error.message, "error");
  const load = async () => { setLoading(true); try { setRecords((await api.attendance({ userId: userId || undefined, from, to })).records); } finally { setLoading(false); } };
  useEffect(() => { load().catch(report); }, [userId, from, to]);
  useEffect(() => { if (canManageUsers) api.users().then((result) => setUsers(result.users)).catch(() => setUsers([])); }, [canManageUsers]);
  useLiveData(load);

  const open = records.filter((record) => !record.punchedOutAt).length;
  const totalMinutes = records.reduce((sum, record) => sum + (record.minutes ?? 0), 0);
  const duration = (minutes: number) => `${Math.floor(minutes / 60)}h ${String(minutes % 60).padStart(2, "0")}m`;
  // Prints the filtered set on screen, not the whole table, so a range or person
  // chosen above is what comes out of the printer.
  function printRecords() {
    const person = userId ? users.find((user) => user.id === userId)?.displayName ?? "Selected person" : "All people";
    const rows = records.map((record) => `<tr><td>${escapeHtml(formatDate(record.punchedInAt))}</td><td>${escapeHtml(record.displayName)}</td><td>${escapeHtml(record.identificationNumber ?? "—")}</td><td>${escapeHtml(record.role.replaceAll("_", " "))}</td><td>${escapeHtml(formatTime(record.punchedInAt))}</td><td>${escapeHtml(record.punchedOutAt ? formatTime(record.punchedOutAt) : "on shift")}</td><td>${escapeHtml(record.minutes === null ? "—" : duration(record.minutes))}</td></tr>`).join("");
    const body = `<section class="page">
      <div class="doc-header"><div><h1>Clock in/out</h1><strong class="doc-number">${escapeHtml(formatDate(from))} – ${escapeHtml(formatDate(to))}</strong></div><div class="brand"><span>Tal-Ahwa</span></div></div>
      <div class="meta"><div><small>People</small><strong>${escapeHtml(person)}</strong></div><div><small>Sessions</small><strong>${records.length}</strong></div><div><small>Still clocked in</small><strong>${open}</strong></div><div><small>Total hours</small><strong>${escapeHtml(duration(totalMinutes))}</strong></div></div>
      <table><thead><tr><th>Date</th><th>Person</th><th>ID</th><th>Role</th><th>Clock in</th><th>Clock out</th><th>Duration</th></tr></thead><tbody>${rows || '<tr><td colspan="7">No records in this period.</td></tr>'}</tbody></table>
    </section>`;
    if (!openPrintWindow(documentShell(`Clock in/out ${formatDate(from)} - ${formatDate(to)}`, body))) onMessage("Enable pop-ups to print this list.", "error");
  }
  const staffOptions = users.filter((user) => user.identificationNumber || records.some((record) => record.userId === user.id));

  return <section className="punches-panel">
    <h2>Clock in/out</h2>
    <p>Attendance records from the clock in/out terminal. Filter by date range or person, then print the list.</p>
    <div className="table-tools order-range-tools">
      <label className="range-field"><span>From</span><DateField label="Clock in/out from" value={from} onChange={setFrom}/></label>
      <label className="range-field"><span>To</span><DateField label="Clock in/out to" value={to} onChange={setTo}/></label>
      <select aria-label="Filter by user" value={userId} onChange={(event) => setUserId(event.target.value)}>
        <option value="">All people</option>
        {staffOptions.map((user) => <option key={user.id} value={user.id}>{user.displayName}{user.identificationNumber ? ` · ${user.identificationNumber}` : ""}</option>)}
      </select>
      <button className="secondary-button" disabled={loading} onClick={() => load().catch(report)}>{loading ? "Loading…" : "Refresh"}</button>
      <button className="secondary-button" disabled={loading || records.length === 0} onClick={printRecords}>Print list</button>
    </div>
    <div className="statement-summary">
      <div><small>Sessions</small><strong>{records.length}</strong></div>
      <div><small>Still clocked in</small><strong>{open}</strong></div>
      <div><small>Total hours</small><strong>{duration(totalMinutes)}</strong></div>
    </div>
    <div className="data-table"><table>
      <thead><tr><th>Date</th><th>Person</th><th>ID</th><th>Role</th><th>Clock in</th><th>Clock out</th><th className="num-cell">Duration</th></tr></thead>
      <tbody>{records.map((record) => <tr key={record.id}>
        <td>{formatDate(record.punchedInAt)}</td>
        <td><strong>{record.displayName}</strong></td>
        <td>{record.identificationNumber ?? "—"}</td>
        <td>{record.role.replaceAll("_", " ")}</td>
        <td>{formatTime(record.punchedInAt)}</td>
        <td>{record.punchedOutAt ? formatTime(record.punchedOutAt) : <span className="status new">on shift</span>}</td>
        <td className="num-cell">{record.minutes === null ? "—" : duration(record.minutes)}</td>
      </tr>)}
      {records.length === 0 && <tr><td colSpan={7} className="empty-cell">No clock in/out records in this period.</td></tr>}</tbody>
    </table></div>
  </section>;
}
